/* eslint-disable @typescript-eslint/no-empty-function */
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import clock from '../../../../assets/images/icons/clock.svg';
import coins from '../../../../assets/images/icons/coins.svg';
import distance from '../../../../assets/images/icons/distance.svg';
import useApiCall from '../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../hooks/useGetGeneralAppData';
import ConsultsService from '../../../../services/ConsultsService';
import floatToCurrency from '../../../../utils/floatToCurrency';
import FilterRadioButton from '../../../FilterRadioButtons';
import { FilterRadioButtonsContainer } from '../../../FilterRadioButtonsContainer';
import MyModal from '../../../Modal';
import { IGetRouteOptionApiResponse, IGetRouteOptionMrApiResponse, SectionOptionType, TrajetoSelecaoVolum, TrajetosIda } from '../../types';
import Route from './components/Route';
import { ButtonContentContainer } from './styles';

interface IChangeReturningOptionModal {
  changeReturningOptionModalShow: boolean;
  otherReturningOptions: TrajetosIda[];
  setChangeReturningOptionModalShow: Dispatch<SetStateAction<boolean>>;
  changeReturningOption: (selectedOption: TrajetosIda) => Promise<void>;
  selectedReturningRoute: TrajetosIda;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  isMultiRoute: boolean;
  selectedSection: SectionOptionType;
}

export default function ChangeReturningOptionModal({
  changeReturningOptionModalShow,
  otherReturningOptions,
  setChangeReturningOptionModalShow,
  changeReturningOption,
  selectedReturningRoute,
  setIsLoading,
  isMultiRoute,
  selectedSection,
}: IChangeReturningOptionModal) {
  const [selectedReturningOption, setSelectedReturningOption] = useState(selectedReturningRoute);
  const [routeDetails, setRouteDetails] = useState<TrajetoSelecaoVolum[]>([]);

  const { apiCall } = useApiCall();
  const { userCode, userProfile, companyCode, usersCompanyCode } = useGetGeneralAppData();
  const { consultCode } = useParams();

  const getCorrespondingRoute = useCallback(async () => {
    await apiCall({
      apiToCall: isMultiRoute ? ConsultsService.getRouteOfOptionMr : ConsultsService.getRouteOfOption,
      queryParams: isMultiRoute
        ?
        {
          codUsu: userCode,
          perfilUsu: userProfile,
          consultCode,
          trajeto: selectedReturningOption.trajeto,
          sequenciaTrajeto: selectedReturningOption.sequenciaTrajeto,
          codEmpresa: usersCompanyCode,
        }
        :
        {
          codUsu: userCode,
          perfilUsu: userProfile,
          codEmpresa: companyCode,
          consultCode,
          sentido: selectedReturningOption.sentido,
          trajeto: selectedReturningOption.trajeto,
          rota: selectedReturningOption.rota,
        },
      actionAfterResponse: isMultiRoute
        ?
        (response: IGetRouteOptionMrApiResponse) => {
          if (!response.codigo) {
            return;
          }
          setRouteDetails(response.trajetoSel?.map((x) => ({ ...x, sentido: x.sentido !== 2 ? 'IDA' : 'VOLTA' })) || []);
        }
        :
        (response: IGetRouteOptionApiResponse) => {
          if (!response.codigo) {
            return;
          }
          setRouteDetails(response.trajetoSelecao);
        },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
    });
  }, [apiCall, companyCode, consultCode, isMultiRoute, selectedReturningOption?.rota, selectedReturningOption?.sentido, selectedReturningOption?.sequenciaTrajeto, selectedReturningOption?.trajeto, setIsLoading, userCode, userProfile, usersCompanyCode]);

  useEffect(() => {
    if (!changeReturningOptionModalShow) {
      return;
    }

    getCorrespondingRoute();
  }, [getCorrespondingRoute, changeReturningOptionModalShow]);

  return (
    <MyModal
      show={changeReturningOptionModalShow}
      title={isMultiRoute ? `Opções de rota - Trecho ${selectedSection.value}` : 'Opções de rota (sentido volta - Trabalho -> Casa)'}
      centeredBody
      modalBody={(
        <>
          <h6 style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '16px' }}>Selecione e confirme a rota desejada</h6>
          <FilterRadioButtonsContainer>
            {otherReturningOptions.map((rt) => (
              <FilterRadioButton
                key={rt.trajeto}
                selected={rt.trajeto === selectedReturningOption.trajeto}
                onClick={() => setSelectedReturningOption(rt)}
                height="auto"
              >
                <ButtonContentContainer>
                  <h6>Opção {rt.trajeto}</h6>
                  <div>
                    <div>
                      <img src={distance} alt="" className='oppositeTheme' />
                      <span>{rt.distanciaTotTj} m</span>
                    </div>
                    <div>
                      <img src={clock} alt="" className='oppositeTheme' />
                      <span>{rt.tempoTotTj} min</span>
                    </div>
                    <div>
                      <img src={coins} alt="" className='oppositeTheme' />
                      <span>{floatToCurrency(rt.valorVtTotal)}</span>
                    </div>
                  </div>
                </ButtonContentContainer>
              </FilterRadioButton>
            ))}
          </FilterRadioButtonsContainer>

          <Route returningRoute={routeDetails} />
        </>
      )}
      type="action"
      closeButtonLabel="Fechar"
      actionButtonLabel="Confirmar rota"
      onClose={() => {
        setChangeReturningOptionModalShow(false);
        setSelectedReturningOption(selectedReturningRoute);
      }}
      onAction={() => changeReturningOption(selectedReturningOption)}
      isActionButtonDisabled={selectedReturningRoute?.trajeto === selectedReturningOption?.trajeto}
    />
  );
}
